import { useState, useEffect, useCallback } from 'react';
import { reportsAPI, claimsAPI, membersAPI } from '../../services/api';

const CURRENT_YEAR = new Date().getFullYear();

function fmtTZS(n) {
  return `TZS ${Number(n || 0).toLocaleString()}`;
}

function toCSV(rows, columns) {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const header = columns.map((c) => esc(c.label)).join(',');
  const body = rows
    .map((r) => columns.map((c) => esc(typeof c.value === 'function' ? c.value(r) : r[c.key])).join(','))
    .join('\n');
  return header + '\n' + body;
}

function downloadCSV(filename, rows, columns) {
  const csv = toCSV(rows, columns);
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Shared report shell: title, toolbar (export/print), loading/error ──
function ReportPanel({ title, onClose, loading, error, onExport, children }) {
  return (
    <div className="mt-6 rounded-[28px] bg-white shadow-[0_20px_50px_rgba(15,23,42,0.08)] p-5 sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-slate-800">{title}</h2>
        <div className="flex items-center gap-2">
          {onExport && (
            <button onClick={onExport}
              className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50">
              ⬇ Export CSV
            </button>
          )}
          <button onClick={() => window.print()}
            className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50">
            🖨 Print / PDF
          </button>
          <button onClick={onClose}
            className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-500 hover:bg-slate-200">
            ✕ Close
          </button>
        </div>
      </div>
      {loading && <p className="py-8 text-center text-sm text-slate-400">Loading…</p>}
      {error && !loading && <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}
      {!loading && !error && children}
    </div>
  );
}

function SimpleTable({ columns, rows, emptyText = 'No data.' }) {
  if (!rows.length) return <p className="py-8 text-center text-sm text-slate-400">{emptyText}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b bg-slate-50">
          <tr>
            {columns.map((c) => (
              <th key={c.label} className="px-4 py-2.5 text-left font-semibold text-slate-600">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b last:border-b-0 hover:bg-slate-50">
              {columns.map((c) => (
                <td key={c.label} className="px-4 py-2.5 text-slate-700">
                  {typeof c.value === 'function' ? c.value(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Payment Reports ──────────────────────────────────────────────────────────

function PaidVsUnpaidPanel({ onClose }) {
  const [years, setYears] = useState([]);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    reportsAPI.paymentsByYear()
      .then((r) => setYears(r.years || []))
      .catch((e) => setError(e.response?.data?.error || 'Failed to load report.'))
      .finally(() => setLoading(false));
  }, []);

  const row = years.find((y) => y.year === year);

  return (
    <ReportPanel title="Paid vs Unpaid" onClose={onClose} loading={loading} error={error}>
      <div className="mb-4 flex items-center gap-2">
        <label className="text-xs font-semibold text-slate-500">Year</label>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))}
          className="rounded-xl border border-slate-200 px-3 py-1.5 text-sm">
          {years.map((y) => <option key={y.year} value={y.year}>{y.year}</option>)}
        </select>
      </div>
      {row ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Total Invoices</p>
            <p className="mt-1.5 text-2xl font-bold text-slate-800">{row.invoice_count.toLocaleString()}</p>
          </div>
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600">Paid</p>
            <p className="mt-1.5 text-2xl font-bold text-emerald-900">{row.paid_count.toLocaleString()}</p>
            <p className="text-xs text-emerald-600">{fmtTZS(row.total_paid)}</p>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-amber-600">Unpaid</p>
            <p className="mt-1.5 text-2xl font-bold text-amber-900">{row.unpaid_count.toLocaleString()}</p>
            <p className="text-xs text-amber-600">{fmtTZS(row.total_outstanding)}</p>
          </div>
        </div>
      ) : <p className="text-sm text-slate-400">No data for {year}.</p>}
    </ReportPanel>
  );
}

function AnnualSummaryPanel({ onClose }) {
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    reportsAPI.paymentsByYear()
      .then((r) => setYears(r.years || []))
      .catch((e) => setError(e.response?.data?.error || 'Failed to load report.'))
      .finally(() => setLoading(false));
  }, []);

  const columns = [
    { label: 'Year', key: 'year' },
    { label: 'Invoices', key: 'invoice_count' },
    { label: 'Paid', key: 'paid_count' },
    { label: 'Unpaid', key: 'unpaid_count' },
    { label: 'Total Invoiced', value: (r) => fmtTZS(r.total_invoiced) },
    { label: 'Total Paid', value: (r) => fmtTZS(r.total_paid) },
    { label: 'Outstanding', value: (r) => fmtTZS(r.total_outstanding) },
  ];

  return (
    <ReportPanel title="Annual Summary" onClose={onClose} loading={loading} error={error}
      onExport={() => downloadCSV('annual_summary.csv', years, columns)}>
      <SimpleTable columns={columns} rows={years} />
    </ReportPanel>
  );
}

function MemberOutstandingPanel({ onClose }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    reportsAPI.memberOutstanding(page, 25, search)
      .then((r) => {
        setMembers(r.members || []);
        setTotalPages(r.total_pages || 1);
        setTotal(r.total || 0);
      })
      .catch((e) => setError(e.response?.data?.error || 'Failed to load report.'))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search]);

  const columns = [
    { label: 'Full Name', key: 'full_name' },
    { label: 'Roll No', key: 'roll_no' },
    { label: 'Phone', key: 'phone' },
    { label: 'Unpaid Invoices', key: 'unpaid_count' },
    { label: 'Years', value: (r) => r.oldest_unpaid_year === r.newest_unpaid_year ? r.oldest_unpaid_year : `${r.oldest_unpaid_year}–${r.newest_unpaid_year}` },
    { label: 'Total Outstanding', value: (r) => fmtTZS(r.total_outstanding) },
  ];

  return (
    <ReportPanel title="Member Outstanding" onClose={onClose} loading={loading} error={error}
      onExport={() => downloadCSV('member_outstanding.csv', members, columns)}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search name or roll no..."
          className="w-full max-w-xs rounded-xl border border-slate-200 px-3 py-2 text-sm" />
        <p className="whitespace-nowrap text-xs text-slate-400">Total: {total.toLocaleString()}</p>
      </div>
      <SimpleTable columns={columns} rows={members} />
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-xs text-slate-400">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
              className="rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-medium disabled:opacity-50">Previous</button>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="rounded-xl bg-slate-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">Next</button>
          </div>
        </div>
      )}
    </ReportPanel>
  );
}

// ── Claim Reports ─────────────────────────────────────────────────────────────

function ClaimsStatusPanel({ onClose }) {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    claimsAPI.stats(year)
      .then(setStats)
      .catch((e) => setError(e.response?.data?.error || 'Failed to load report.'))
      .finally(() => setLoading(false));
  }, [year]);

  const cells = stats ? [
    ['Submitted', stats.submitted, 'slate'],
    ['Under Review', stats.under_review, 'blue'],
    ['Approved', stats.approved, 'indigo'],
    ['Rejected', stats.rejected, 'red'],
    ['Paid', stats.paid, 'emerald'],
  ] : [];

  return (
    <ReportPanel title="Claims Status" onClose={onClose} loading={loading} error={error}>
      <div className="mb-4 flex items-center gap-2">
        <label className="text-xs font-semibold text-slate-500">Year</label>
        <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))}
          className="w-24 rounded-xl border border-slate-200 px-3 py-1.5 text-sm" />
      </div>
      {stats && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
            {cells.map(([label, val]) => (
              <div key={label} className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-center">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">{label}</p>
                <p className="mt-1 text-xl font-bold text-slate-800">{val}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-slate-600">
            Total claims: <strong>{stats.total}</strong> · Total paid amount: <strong>{fmtTZS(stats.total_paid_amount)}</strong>
          </p>
        </>
      )}
    </ReportPanel>
  );
}

function BeneficiaryAnalysisPanel({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    reportsAPI.beneficiaryAnalysis()
      .then(setData)
      .catch((e) => setError(e.response?.data?.error || 'Failed to load report.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <ReportPanel title="Beneficiary Analysis" onClose={onClose} loading={loading} error={error}>
      {data && (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-400">Overview</p>
            <p className="text-sm text-slate-700">Total beneficiaries: <strong>{data.total_beneficiaries}</strong></p>
            <p className="mt-1 text-sm text-amber-700">Members with none on file: <strong>{data.members_with_no_beneficiary}</strong></p>
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-400">By Relationship</p>
            <SimpleTable
              columns={[{ label: 'Relationship', key: 'relationship' }, { label: 'Count', key: 'count' }]}
              rows={data.by_relationship} />
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-400">By Gender</p>
            <SimpleTable
              columns={[{ label: 'Gender', key: 'gender' }, { label: 'Count', key: 'count' }]}
              rows={data.by_gender} />
          </div>
        </div>
      )}
    </ReportPanel>
  );
}

function PaidClaimsPanel({ onClose }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(() => {
    setLoading(true);
    claimsAPI.payoutRegister(page, search)
      .then((r) => {
        setRecords(r.records || []);
        setPages(r.pages || 1);
        setTotalAmount(r.total_amount || 0);
      })
      .catch((e) => setError(e.response?.data?.error || 'Failed to load report.'))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search]);

  const columns = [
    { label: 'Claim No', key: 'claim_no' },
    { label: 'Member', key: 'member_name' },
    { label: 'Roll No', key: 'roll_no' },
    { label: 'Beneficiary', key: 'beneficiary_name' },
    { label: 'Relationship', key: 'relationship' },
    { label: 'Amount', value: (r) => fmtTZS(r.amount) },
    { label: 'Paid On', key: 'payment_date' },
  ];

  return (
    <ReportPanel title="Paid Claims" onClose={onClose} loading={loading} error={error}
      onExport={() => downloadCSV('paid_claims.csv', records, columns)}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search claim no, member, roll no..."
          className="w-full max-w-xs rounded-xl border border-slate-200 px-3 py-2 text-sm" />
        <p className="whitespace-nowrap text-xs text-slate-400">Total paid (all-time): {fmtTZS(totalAmount)}</p>
      </div>
      <SimpleTable columns={columns} rows={records} />
      {pages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-xs text-slate-400">Page {page} of {pages}</p>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
              className="rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-medium disabled:opacity-50">Previous</button>
            <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page === pages}
              className="rounded-xl bg-slate-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">Next</button>
          </div>
        </div>
      )}
    </ReportPanel>
  );
}

// ── Member Reports ────────────────────────────────────────────────────────────

function MemberListingPanel({ onClose }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(() => {
    setLoading(true);
    membersAPI.list(page, 25, search)
      .then((r) => {
        setMembers(r.members || []);
        setTotalPages(r.total_pages || 1);
        setTotal(r.total || 0);
      })
      .catch((e) => setError(e.response?.data?.error || 'Failed to load report.'))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search]);

  const columns = [
    { label: 'Full Name', key: 'full_name' },
    { label: 'Roll No', key: 'roll_no' },
    { label: 'Email', key: 'email' },
    { label: 'Phone', key: 'phone' },
    { label: 'Status', key: 'status' },
    { label: 'Chapter', key: 'chapter' },
    { label: 'Firm', key: 'firm_name' },
  ];

  async function handleExportAll() {
    setExporting(true);
    try {
      const r = await reportsAPI.membersExport();
      downloadCSV('member_listing.csv', r.members || [], columns);
    } catch (e) {
      alert(e.response?.data?.error || 'Export failed.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <ReportPanel title="Member Listing" onClose={onClose} loading={loading} error={error}
      onExport={handleExportAll}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search name or roll no..."
          className="w-full max-w-xs rounded-xl border border-slate-200 px-3 py-2 text-sm" />
        <p className="whitespace-nowrap text-xs text-slate-400">
          {exporting ? 'Preparing export…' : `Total: ${total.toLocaleString()} (export downloads all)`}
        </p>
      </div>
      <SimpleTable columns={columns} rows={members} />
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-xs text-slate-400">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
              className="rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-medium disabled:opacity-50">Previous</button>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="rounded-xl bg-slate-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">Next</button>
          </div>
        </div>
      )}
    </ReportPanel>
  );
}

function AgeAnalysisPanel({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    reportsAPI.ageAnalysis()
      .then(setData)
      .catch((e) => setError(e.response?.data?.error || 'Failed to load report.'))
      .finally(() => setLoading(false));
  }, []);

  const maxCount = data ? Math.max(1, ...data.buckets.map((b) => b.count)) : 1;

  return (
    <ReportPanel title="Age Analysis" onClose={onClose} loading={loading} error={error}
      onExport={data ? () => downloadCSV('age_analysis.csv', data.buckets, [{ label: 'Age Range', key: 'bucket' }, { label: 'Count', key: 'count' }]) : undefined}>
      {data && (
        <>
          <div className="space-y-2.5">
            {data.buckets.map((b) => (
              <div key={b.bucket} className="flex items-center gap-3">
                <span className="w-16 shrink-0 text-xs font-semibold text-slate-500">{b.bucket}</span>
                <div className="h-6 flex-1 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-purple-500"
                    style={{ width: `${(b.count / maxCount) * 100}%` }} />
                </div>
                <span className="w-14 shrink-0 text-right text-xs font-bold text-slate-700">{b.count.toLocaleString()}</span>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-slate-400">Members with no date of birth on file: {data.unknown.toLocaleString()}</p>
        </>
      )}
    </ReportPanel>
  );
}

function ActiveMembersPanel({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    reportsAPI.memberStatusBreakdown()
      .then(setData)
      .catch((e) => setError(e.response?.data?.error || 'Failed to load report.'))
      .finally(() => setLoading(false));
  }, []);

  const columns = [
    { label: 'Status', key: 'status' },
    { label: 'Count', key: 'count' },
    { label: '% of Total', value: (r) => data ? `${((r.count / data.total) * 100).toFixed(1)}%` : '' },
  ];

  return (
    <ReportPanel title="Active Members" onClose={onClose} loading={loading} error={error}
      onExport={data ? () => downloadCSV('member_status_breakdown.csv', data.statuses, columns) : undefined}>
      {data && (
        <>
          <p className="mb-4 text-sm text-slate-600">Total members: <strong>{data.total.toLocaleString()}</strong></p>
          <SimpleTable columns={columns} rows={data.statuses} />
        </>
      )}
    </ReportPanel>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

const REPORTS = {
  paid_vs_unpaid:      { label: 'Paid vs Unpaid',       category: 'payment', Component: PaidVsUnpaidPanel },
  annual_summary:      { label: 'Annual Summary',        category: 'payment', Component: AnnualSummaryPanel },
  member_outstanding:  { label: 'Member Outstanding',    category: 'payment', Component: MemberOutstandingPanel },
  claims_status:       { label: 'Claims Status',         category: 'claim',   Component: ClaimsStatusPanel },
  beneficiary_analysis:{ label: 'Beneficiary Analysis',  category: 'claim',   Component: BeneficiaryAnalysisPanel },
  paid_claims:         { label: 'Paid Claims',           category: 'claim',   Component: PaidClaimsPanel },
  member_listing:      { label: 'Member Listing',        category: 'member',  Component: MemberListingPanel },
  age_analysis:        { label: 'Age Analysis',          category: 'member',  Component: AgeAnalysisPanel },
  active_members:      { label: 'Active Members',        category: 'member',  Component: ActiveMembersPanel },
};

function ReportButton({ reportKey, active, onClick, colorClass }) {
  return (
    <button onClick={onClick}
      className={`w-full py-2 rounded font-semibold transition-colors ${
        active ? 'ring-2 ring-offset-1 ring-slate-800' : ''
      } ${colorClass}`}>
      {REPORTS[reportKey].label}
    </button>
  );
}

export default function Reports() {
  const [active, setActive] = useState(null);

  const toggle = (key) => setActive((cur) => (cur === key ? null : key));
  const ActiveComponent = active ? REPORTS[active].Component : null;

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Reports</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Payment Reports</h2>
          <div className="space-y-2">
            {['paid_vs_unpaid', 'annual_summary', 'member_outstanding'].map((k) => (
              <ReportButton key={k} reportKey={k} active={active === k} onClick={() => toggle(k)}
                colorClass="bg-blue-600 text-white hover:bg-blue-700" />
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Claim Reports</h2>
          <div className="space-y-2">
            {['claims_status', 'beneficiary_analysis', 'paid_claims'].map((k) => (
              <ReportButton key={k} reportKey={k} active={active === k} onClick={() => toggle(k)}
                colorClass="bg-green-600 text-white hover:bg-green-700" />
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Member Reports</h2>
          <div className="space-y-2">
            {['member_listing', 'age_analysis', 'active_members'].map((k) => (
              <ReportButton key={k} reportKey={k} active={active === k} onClick={() => toggle(k)}
                colorClass="bg-purple-600 text-white hover:bg-purple-700" />
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Exports</h2>
          <p className="text-sm text-slate-500">
            Open any report above, then use its <strong>⬇ Export CSV</strong> button (opens in Excel) or
            <strong> 🖨 Print / PDF</strong> button to save it as a PDF via your browser's print dialog.
          </p>
        </div>
      </div>

      {ActiveComponent && <ActiveComponent onClose={() => setActive(null)} />}
    </div>
  );
}
